// The scene's pose source while a voice runs: the module's own pose_at, called
// on the main-thread instance of the voice's wasm (pose-instance.ts, #28) with
// the heard slow state.
//
// The scene binds its joints to a pose source's drivers once, when the model
// loads (engine-scene.ts), so this source keeps the drivers of the source it
// falls back to (the pose table, pose.ts) and maps pose_at's values onto them
// by name. It falls back to the table while no voice runs, while the voice's
// pose instance is being built, and for a voice of another module than the
// model's or one lacking any of the table's drivers.
//
// poseInstanceFor() builds that instance from the init the voice was started
// with, so both run the same module on the same data and give the same bits
// (web/tests/equality/); web/tests/sync/pose.spec.ts checks the poses drawn
// against pose_at at the heard frame.

import type { AudioEngine } from "../audio/engine";
import { PoseInstance } from "../audio/pose-instance";
import { MAX_SLOW } from "../audio/protocol";
import { wrapCycle, type PoseSource } from "../scene/pose";

export type PoseOrigin = "pose_at" | "table";

export class SyncedPose implements PoseSource {
  readonly drivers: readonly string[];
  /** The slow state pose_at gets, in the order the voice's telemetry carries it (ready.slow). */
  readonly slow = new Float64Array(MAX_SLOW);
  private readonly fallback: PoseSource;
  private instance: PoseInstance | null = null;
  /** map[d]: the index in pose_at's values of the fallback's driver d. */
  private map = new Int32Array(0);
  private values = new Float64Array(0);

  constructor(fallback: PoseSource) {
    this.fallback = fallback;
    this.drivers = fallback.drivers;
  }

  /** Where the poses come from now. */
  get origin(): PoseOrigin {
    return this.instance === null ? "table" : "pose_at";
  }

  /** The pose instance in use, or null. */
  get poseInstance(): PoseInstance | null {
    return this.instance;
  }

  /**
   * Takes poses from `instance` (or from the table again, with null). Returns
   * the drivers of the table that pose_at lacks; with any missing, the table stays.
   * The caller owns the instance and frees it.
   */
  use(instance: PoseInstance | null): string[] {
    if (instance === null) {
      this.instance = null;
      return [];
    }
    const index = new Map(instance.drivers.map((d, i) => [d, i]));
    const missing = this.drivers.filter((d) => !index.has(d));
    if (missing.length > 0) return missing;
    this.map = Int32Array.from(this.drivers, (d) => index.get(d)!);
    this.values = new Float64Array(instance.drivers.length);
    this.instance = instance;
    return [];
  }

  /** Sets the slow state for the next poses (from the heard sample); the values past `count` are 0. */
  setSlow(slow: ArrayLike<number>, count: number): void {
    for (let s = 0; s < MAX_SLOW; s++) this.slow[s] = s < count ? slow[s]! : 0;
  }

  sample(theta: number, out: Float64Array): void {
    const instance = this.instance;
    if (instance === null) {
      this.fallback.sample(theta, out);
      return;
    }
    instance.poseAt(wrapCycle(theta), this.slow, this.values);
    for (let d = 0; d < this.map.length; d++) out[d] = this.values[this.map[d]!]!;
  }
}

/**
 * A main-thread pose instance of the voice an engine runs, built from the init
 * it was started with; null for a processor without wasm (the mock).
 */
export async function poseInstanceFor(engine: AudioEngine): Promise<PoseInstance | null> {
  const init = engine.init;
  if (init === null || init.wasm === undefined) return null;
  return PoseInstance.create({
    wasm: init.wasm,
    bundle: init.bundle,
    assets: engine.startAssets,
    ...(init.job !== undefined
      ? { job: init.job }
      : { powertrain: init.powertrain, ...(init.timeline !== undefined ? { timeline: init.timeline } : {}) }),
    sampleRate: engine.context.sampleRate,
  });
}
